import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { EventType, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  EEntityType,
  ERelationType,
  ICorporateDesign,
  ShopTicket,
} from '@web/dtos';
import { ShoppingCartService } from '../../../services/shopping-cart/shopping-cart.service';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { SubscriptionConfigStore } from '../../../services/api/subscription/subscription-config-store.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { ERoute } from '../../../constants/routes.enum';
import { ProgressTrackerSupportService } from '../../../services/support/progress-tracker-support/progress-tracker-support.service';

export interface IProgressBarStep {
  label: string;
  stepIndex: number;
  url: string;
}

@Component({
  selector: 'aw-ticket-configuration-progress-tracker',
  imports: [
    ButtonComponent,
    NgTemplateOutlet,
    NgStyle,
    NgClass,
    AppendTestIdPipe,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './ticket-configuration-progress-tracker.component.html',
})
export class TicketConfigurationProgressTrackerComponent implements OnDestroy {
  public readonly progressBarContent: InputSignal<IProgressBarStep[]> =
    input.required();
  public readonly backLink: InputSignal<ERoute> = input.required();
  public readonly completionTextRelation: InputSignal<
    ERelationType | undefined
  > = input<ERelationType>();
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly router: Router = inject(Router);
  private readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  private readonly subscriptionConfigStore: SubscriptionConfigStore = inject(
    SubscriptionConfigStore
  );
  private readonly progressTracker: ProgressTrackerSupportService = inject(
    ProgressTrackerSupportService
  );

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public readonly ticketConfiguration: Signal<ShopTicket> = this
    .shoppingCartService.directTicketConfiguration as Signal<ShopTicket>;

  public readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;

  public readonly step: WritableSignal<number> =
    this.progressTracker.currentStep;

  private readonly subscriptions: Subscription = new Subscription();

  constructor() {
    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event.type === EventType.NavigationEnd) {
          this.setProgressTrackerState();
        }
      })
    );

    effect(() => {
      if (this.progressBarContent()) {
        this.setProgressTrackerState();
      }
    });
  }

  protected navigateBack(): void {
    const targetStep: any =
      this.progressBarContent().find(
        progressPoint => progressPoint.stepIndex === this.step() - 1
      ) ?? this.progressBarContent()[0];
    this.router.navigateByUrl(targetStep.url);
  }

  protected cancelTicketConfiguration(): void {
    if (this.shoppingCartService.ticketsInCart().length === 1) {
      this.shoppingCartService.removeTicket(0);
      this.router.navigateByUrl(`/${this.backLink()}`);
    } else if (this.shoppingCartService.ticketsInCart().length > 0) {
      this.router.navigateByUrl(`/${this.backLink()}`);
    } else if (this.backLink() === ERoute.subscriptions) {
      this.subscriptionConfigStore.loadSubscriptionConfiguration(undefined);
      this.router.navigateByUrl(`/${this.backLink()}`);
    } else {
      this.router.navigateByUrl(`/${this.backLink()}`);
    }
  }

  protected isCompletionStep(): boolean {
    const progressBarContent = this.progressBarContent();
    const lastStep = progressBarContent[progressBarContent.length - 1];
    return (
      this.completionTextRelation() !== undefined &&
      lastStep?.stepIndex === this.step()
    );
  }

  protected actionTextRelation(): ERelationType {
    return this.isCompletionStep()
      ? (this.completionTextRelation() as ERelationType)
      : ERelationType.cancel;
  }

  protected actionTextEntity(): EEntityType {
    return this.isCompletionStep()
      ? EEntityType.onDemandTicketConfiguration
      : EEntityType.generic;
  }

  private setProgressTrackerState(): void {
    const url: string = this.router.url;
    this.step.set(
      this.progressBarContent().find(progressPoint =>
        url.includes(progressPoint.url)
      )?.stepIndex ?? -1
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
